import React, { useMemo, useRef, useState } from 'react';
import { useStateProgramContext } from './StateProgramContext';
import { JsonFormsCore } from '@jsonforms/core';
import { useLocalization } from '../../store/utils/useLocalization';
import { reactNativeCells, reactNativeRenderers } from '../form/json-forms/renderers';
import { JsonForms } from '@jsonforms/react';

interface StateProgramFormStepProps {
  /**
   * Component to delegate form operations to.
   * Can be JsonForms itself or any form with compatible interface (such as a mock).
   * Avoid adding re-renders between consumer and JSON Forms itself.
   */
  formDelegate: typeof JsonForms;
  /**
   * Observer for when form changes.
   * Does not override internal handling of changes, and changes
   * will still get saved to program data.
   */
  onChange?: () => void;
}

export function StateProgramFormStep(props: StateProgramFormStepProps) {
  const programServices = useStateProgramContext();

  // seed the program with any existing state but track it separately
  // so that we maintain the state even if the form doesn't validate
  const [unsavedData, setUnsavedData] = useState(programServices.stepAttestationData);

  // don't show errors until user interacts with form
  const hasLoadedInitialData = useRef(false);
  const [allowErrorDisplay, setAllowErrorDisplay] = useState(false);

  const { locale } = useLocalization();
  const translate = useMemo(() => programServices.createStepTranslator(locale), [locale]);

  const onChange = (state: Pick<JsonFormsCore, 'data' | 'errors'>) => {
    programServices.changeStepData(state);
    props.onChange?.();
    setUnsavedData(state.data);

    if (!hasLoadedInitialData.current) {
      // keep track of whether initial data has loaded so we know that next change is user input
      hasLoadedInitialData.current = true;
    } else {
      // only allow showing of errors after user first interacts with the form
      setAllowErrorDisplay(true);
    }
  };

  return (
    <props.formDelegate
      // use form config from state program
      schema={programServices.stepAttestationSchema}
      uischema={programServices.stepUiSchema}
      // update local state and try to save form if it validates
      data={unsavedData}
      onChange={onChange}
      // use validation from Wizard Engine instead of JSON Forms built-in
      validationMode="NoValidation"
      additionalErrors={allowErrorDisplay ? programServices.stepValidationErrors : []}
      // augment with custom components to work with our RN app
      renderers={reactNativeRenderers}
      cells={reactNativeCells}
      i18n={{ locale: locale, translate: translate }}
    />
  );
}
